import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import { API_CONFIG } from '../../../config/api';
import { 
  setGlobalToken,
  InvoiceBalanceItem,
  InvoiceBalanceFilter
} from '../lib/TaxInvoiceBalance';
import { loadCustomersForTaxList, Customer } from '../lib/CustomersLoadEntId';
import { getTokenInfo, logTokenInfo, setupTokens, checkAuthentication } from '../../../config/novaqToken';

// Tax Invoice Balance API function
const getTaxInvoiceBalance = async (entId: string, type: 'negative' | 'positive' = 'negative'): Promise<any> => {
  try {
    console.log('🔍 Getting tax invoice balance for entId:', entId, 'type:', type);
    
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/tax-invoice/${type}-balance?entId=${entId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('novaqToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Tax invoice balance response:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error getting tax invoice balance:', error);
    throw error;
  }
};


const CustomersTaxInvoiceList: React.FC = () => {
  const { userAuth } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Data state
  const [negativeBalanceData, setNegativeBalanceData] = useState<InvoiceBalanceItem[]>([]);
  const [positiveBalanceData, setPositiveBalanceData] = useState<InvoiceBalanceItem[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
 
  const [currentFilter, setCurrentFilter] = useState<InvoiceBalanceFilter>({
    entId: '', // Will be set from localStorage
    page: 0,
    size: 10,
    sort: 'createdDate,desc'
  });
  
  const [showStats, setShowStats] = useState(false);
  
  // Token state
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  

  // Load all customers data using Tax Invoice Balance API
  const loadAllCustomersData = useCallback(async () => {
    try {
      console.log('🔄 Loading all customers data using Tax Invoice Balance API...');
      setLoading(true);
      setError(null);
      
      // First load customers
      const customersData = await loadCustomersForTaxList(setCustomers, setLoading, setError);
      if (customersData.length === 0) {
        console.log('❌ No customers available');
        return;
      }
      
      const allNegativeData: any[] = [];
      const allPositiveData: any[] = [];
      
      // Log all EntIds before processing
      console.log('--- Processing Customer EntIds with Tax Invoice Balance API ---');
      const processingEntIdList: string[] = [];
      customersData.forEach((customer: any, index: number) => {
        const entId = customer.EntId || customer.entId;
        const entIdValue = entId || 'NULL';
        processingEntIdList.push(entIdValue);
        console.log(`Processing Customer ${index + 1} (${customer.CustomerName}): EntId = ${entIdValue}`);
      });
      console.log('📋 Processing EntId жагсаалт:', processingEntIdList);
      console.log('📊 Нийт processing EntId тоо:', processingEntIdList.length);
      console.log('📊 NULL биш processing EntId тоо:', processingEntIdList.filter(id => id !== 'NULL').length);
      console.log('-----------------------------------');
      
      // Бүх customer-ийн data авах using Tax Invoice Balance API
      for (const customer of customersData) {
        try {
          // Use EntId field from customer data only
          const entId = customer.EntId || customer.entId;
          if (!entId) {
            console.error(`❌ Customer EntId is NULL for ${customer.CustomerName}:`, customer);
            continue; // Skip this customer
          }
          console.log(`🔄 Loading tax invoice balance for customer: ${customer.CustomerName} (EntId: ${entId})`);
          
          // Get negative balance data
          const negativeData = await getTaxInvoiceBalance(entId, 'negative');
          if (negativeData.success && negativeData.data) {
            let dataArray = [];
            if (negativeData.data._embedded && negativeData.data._embedded.vActInvoiceBalances) {
              dataArray = negativeData.data._embedded.vActInvoiceBalances;
              console.log(`📊 Found negative balance data for ${customer.CustomerName}:`, dataArray.length, 'items');
            } else if (Array.isArray(negativeData.data)) {
              dataArray = negativeData.data;
              console.log(`📊 Found direct negative balance array for ${customer.CustomerName}:`, dataArray.length, 'items');
            }
            
            if (dataArray.length > 0) {
              const customerData = dataArray.map((item: any) => ({
                ...item,
                customerName: customer.CustomerName,
                customerId: customer.CustomerID,
                customerTin: customer.TinCode
              }));
              allNegativeData.push(...customerData);
              console.log(`✅ Negative balance data loaded for ${customer.CustomerName}: ${customerData.length} items`);
            }
          }
          
          // Get positive balance data
          const positiveData = await getTaxInvoiceBalance(entId, 'positive');
          if (positiveData.success && positiveData.data) {
            let dataArray = [];
            if (positiveData.data._embedded && positiveData.data._embedded.vActInvoiceBalances) {
              dataArray = positiveData.data._embedded.vActInvoiceBalances;
              console.log(`📊 Found positive balance data for ${customer.CustomerName}:`, dataArray.length, 'items');
            } else if (Array.isArray(positiveData.data)) {
              dataArray = positiveData.data;
              console.log(`📊 Found direct positive balance array for ${customer.CustomerName}:`, dataArray.length, 'items');
            }
            
            if (dataArray.length > 0) {
              const customerData = dataArray.map((item: any) => ({
                ...item,
                customerName: customer.CustomerName,
                customerId: customer.CustomerID,
                customerTin: customer.TinCode
              }));
              allPositiveData.push(...customerData);
              console.log(`✅ Positive balance data loaded for ${customer.CustomerName}: ${customerData.length} items`);
            }
          }
          
          console.log(`✅ Tax invoice balance data loaded for customer: ${customer.CustomerName}`);
          
        } catch (error) {
          console.error(`❌ Error loading tax invoice balance data for customer ${customer.CustomerName}:`, error);
        }
      }
      
      // Бүх data-г state-д хадгалах
      setNegativeBalanceData(allNegativeData);
      setPositiveBalanceData(allPositiveData);
      
      // Statistics тооцоолох
      const stats = {
        totalCustomers: customersData.length,
        negativeCount: allNegativeData.length,
        positiveCount: allPositiveData.length,
        totalItems: allNegativeData.length + allPositiveData.length
      };
      setStats(stats);
      
      console.log('✅ All customers tax invoice balance data loaded successfully:', stats);
      
    } catch (error) {
      console.error('❌ Error loading all customers tax invoice balance data:', error);
      setError('Бүх customer-ийн tax invoice balance data авах алдаа: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleSearch = useCallback(() => {
    setShowColumnSearch(prev => !prev);
  }, []);

  const handleColumnSettings = useCallback(() => {
    setShowColumnSettings(prev => !prev);
  }, []);

  const handleRefresh = useCallback(() => {
    console.log('🔄 SmartTableButtons refresh triggered');
    loadAllCustomersData();
  }, [loadAllCustomersData]);

  const handleToggleStats = useCallback(() => {
    setShowStats(prev => !prev);
  }, []);




  // Load token from localStorage on component mount
  useEffect(() => {
    console.log('🔍 Loading token from localStorage...');
    
    // Token мэдээлэл авах
    const tokenInfo = getTokenInfo();
    logTokenInfo(tokenInfo);
    
    const username = localStorage.getItem('username');
    const accountantOid = localStorage.getItem('accountantOid');
    
    console.log('🔍 localStorage мэдээлэл:');
    console.log('  - username:', username || 'null');
    console.log('  - accountantOid:', accountantOid || 'null');
    
    if (tokenInfo.hasValidToken) {
      setHasToken(true);
      console.log('✅ Token loaded successfully, hasToken set to true');
      
      // accountantOid-г entId болгон тохируулах - ЭНЭ НЬ БУРУУ!
      // EntId-г customer мэдээллээс авч байх ёстой, AccountantOid-оос биш
      console.log('⚠️ WARNING: Using accountantOid as entId is incorrect!');
      console.log('⚠️ EntId should come from customer data, not AccountantOid');
      if (accountantOid) {
        setCurrentFilter(prev => ({
          ...prev,
          entId: accountantOid
        }));
        console.log('✅ accountantOid set as entId (INCORRECT):', accountantOid);
      }
      
      // TaxToken-г global-д тохируулах
      setupTokens(tokenInfo);
      if (tokenInfo.primaryToken) {
        setGlobalToken(tokenInfo.primaryToken);
      }
      
      // Load customers first, then data
      console.log('🔄 Loading customers first...');
      loadCustomersForTaxList(setCustomers, setLoading, setError, () => {
        console.log('✅ Customers loaded, now loading data...');
        // Load data after customers are loaded
        loadAllCustomersData();
      });
    } else {
      console.log('❌ No valid token found');
      setHasToken(false);
      setError('Хэрэглэгч нэвтрээгүй эсвэл token олдсонгүй. Дахин нэвтрэнэ үү.');
      // Redirect to home page
      console.log('🔄 Redirecting to home page...');
      navigate('/');
    }
  }, [navigate]);



  // Get current data
  const getCurrentData = () => {
    return [...negativeBalanceData, ...positiveBalanceData];
  };

 


  // Table columns configuration for Tax Invoice Balance
  const columns: SmartTableColumn[] = [   
    { key: 'customerId', label: 'Харилцагчийн регистр', width: 150}, 
    { key: 'customerName', label: 'Харилцагчийн нэр', width: 150},
    { key: 'invoiceNo', label: 'Нэхэмжлэх дугаар', width: 150},
    { key: 'taxTypeName', label: 'Төрөл', width: 150},
    { key: 'createdDate', label: 'Үүсгэсэн огноо', width: 150},      
    { key: 'year', label: 'Он', width: 80},   
    { key: 'period', label: 'Хугацаа', width: 100},
    { 
      key: 'balance', 
      label: 'Үлдэгдэл дүн', 
      width: 150,
      render: (value: any) => {
        if (value === null || value === undefined || value === '') {
          return <div style={{ textAlign: 'right', color: '#6B7280' }}>-</div>;
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return <div style={{ textAlign: 'right', color: '#6B7280' }}>{value}</div>;
        }
        
        let color = '#6B7280'; // Default gray for zero
        if (numValue < 0) {
          color = '#EF4444'; // Red for negative values
        } else if (numValue > 0) {
          color = '#10B981'; // Green for positive values
        }
        
        return (
          <div style={{ textAlign: 'right', color }}>
            {new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(numValue)}
          </div>
        );
      }
    },  
  ];

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Татварын нэхэмжлэхийн жагсаалт
          </h1>
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}           
            addable={false}
          />
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <SmartTable
              data={getCurrentData()}
              columns={columns}
              fetchData={() => {
                console.log('🔄 SmartTable fetchData called');
                console.log('🔄 Calling loadAllCustomersData from fetchData');
                loadAllCustomersData();
              }}
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
              settingsKey="tax-invoice-balance"
              rowKey="id"
              editable={false}
            />
          </div>
        </div>

        {/* Stats Panel */}
        {showStats && stats && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-blue-800 mb-3">Статистик</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-blue-700">
              <div>
                <div className="font-medium text-blue-900">Нийт харилцагч</div>
                <div className="text-2xl font-bold text-blue-800">{stats.totalCustomers}</div>
              </div>
              <div>
                <div className="font-medium text-blue-900">Сөрөг үлдэгдэлтэй нэхэмжлэх</div>
                <div className="text-2xl font-bold text-red-600">{stats.negativeCount}</div>
              </div>
              <div>
                <div className="font-medium text-blue-900">Эерэг үлдэгдэлтэй нэхэмжлэх</div>
                <div className="text-2xl font-bold text-green-600">{stats.positiveCount}</div>
              </div>
              <div>
                <div className="font-medium text-blue-900">Нийт бүртгэл</div>
                <div className="text-2xl font-bold text-blue-800">{stats.totalItems}</div>
              </div>
            </div>
          </div>
        )}

        {/* Success/Error Messages */}
        {success && (
          <div className="fixed top-4 right-4 z-50">
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          </div>
        )}
        
        {error && (
          <div className="fixed top-4 right-4 z-50">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default CustomersTaxInvoiceList;
